import { useCallback, useMemo } from 'react';
import { pick, prop } from 'ramda';

import { useFilterByRoleAvailability } from '@core/js/components/main/NavigationBar/hooks/useFilterByRoleAvailability';
import { IMenuItemConfig } from '@core/js/components/system/App/store/types';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';
import { getCleanUrl, getFriendlyUrl } from '@core/js/utils/url.util';

import { checkAvailablePage } from '@interlayer/js/app/hooks/menu/helpers';
import { useDynamicTagList } from '@interlayer/js/app/hooks/useDynamicTagList';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { deepFind } from '@interlayer/js/app/utils/methods.util';
import { usePageContext } from '@interlayer/js/components/system/Page/hocs/withCommonPage/context.buzz';

import { TCheckMenuFn, TCheckPermissions } from './types';

const hiddenClassByPlayerTag = 'hidden-tag';

const checkPermissions = ({ page, isAdminLoggedIn, isLoggedIn }: TCheckPermissions): boolean => {
    const isGuestRole = Boolean(page?.roles?.includes('GUEST'));
    const isUserRole = Boolean(page?.roles?.includes('USER'));

    return isAdminLoggedIn
        || isGuestRole && !isUserRole && !isLoggedIn
        || isUserRole && !isGuestRole && isLoggedIn
        || isUserRole && isGuestRole;
};

export const useCheckMenuItemByTag = () => {
    const { allPlayerTags, isLoggedIn } = useUserSelector(pick(['allPlayerTags', 'isLoggedIn']));
    const dTagPlayerTagsForHidden = useDynamicTagList({
        tagName: DYNAMIC_TAG_NAMES.playerTagsForHidden,
        withLowerCase: false
    });

    return useCallback((cssClasses: string): boolean => {
        if (!isLoggedIn || !allPlayerTags || !cssClasses?.includes(hiddenClassByPlayerTag)) {
            return true;
        }

        return !dTagPlayerTagsForHidden.some(playerTag => allPlayerTags[playerTag]);
    }, [allPlayerTags, dTagPlayerTagsForHidden, isLoggedIn]);
};

export const useMenu = (checkMenu?: TCheckMenuFn) => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const checkMenuItemByTag = useCheckMenuItemByTag();
    const menuItems = useAppSelector(prop('menuItems'));
    const isAdminLoggedIn = useAppSelector(prop('isAdminLoggedIn'));

    const checkPages = useCallback((data) => {
        return checkPermissions({ page: data, isAdminLoggedIn, isLoggedIn })
            && checkAvailablePage(data?.cssClasses)
            && checkMenuItemByTag(data?.cssClasses)
            && (checkMenu ? checkMenu(data) : true);
    }, [checkMenu, checkMenuItemByTag, isAdminLoggedIn, isLoggedIn]);

    return useMemo(() => {
        return menuItems.reduce((acc, item) => {
            if (checkPages(item)) {
                acc.push({
                    ...item,
                    children: item?.children?.filter(checkPages) || []
                });
            }

            return acc;
        }, [] as IMenuItemConfig[]);
    }, [checkPages, menuItems]);
};

const useCurrentPageURL = () => {
    const rootPage = useAppSelector(prop('rootPage'));

    return useCallback((friendlyURL) => {
        const pageUrl = friendlyURL !== undefined ? friendlyURL : getCleanUrl();

        if (!pageUrl || getCleanUrl(pageUrl) === '/') {
            return getCleanUrl(rootPage);
        }

        return getCleanUrl(pageUrl);
    }, [rootPage]);
};

const useFilteredItems = () => {
    const filterByRoleAvailability = useFilterByRoleAvailability();
    const getCurrentPageURL = useCurrentPageURL();
    const menuItems = useAppSelector(prop('menuItems'));

    return useCallback((find, friendlyURL) =>
        find(filterByRoleAvailability(menuItems), getCurrentPageURL(friendlyURL)),
    [filterByRoleAvailability, getCurrentPageURL, menuItems]);
};

export const useFindItemMenu = (withoutFilterByRole?: boolean) => {
    const menuItems = useAppSelector(prop('menuItems'));
    const { path: pagePath } = usePageContext();
    const getFilteredItems = useFilteredItems();
    const getCurrentPageURL = useCurrentPageURL();
    const pageFriendPath = getFriendlyUrl(pagePath);

    return useCallback((friendlyURL: string = pageFriendPath): IMenuItemConfig => {
        const find = (items: IMenuItemConfig[], path: string) => {
            const isSubTournament = new RegExp('^/tournaments/[^S?][^S]+$', 'g').test(path);
            const source = isSubTournament ? '/tournaments' : path;

            return deepFind({ items, childrenKey: 'children', sourceKey: 'friendlyURL', source });
        };

        return withoutFilterByRole
            ? find(menuItems, getCurrentPageURL(friendlyURL))
            : getFilteredItems(find, friendlyURL);
    }, [getCurrentPageURL, getFilteredItems, menuItems, pageFriendPath, withoutFilterByRole]);
};

export const useCurrentItemMenu = (defaultFriendlyURL?: string) => {
    const findItemMenu = useFindItemMenu();

    return useMemo(() => findItemMenu(defaultFriendlyURL), [defaultFriendlyURL, findItemMenu]);
};

export const useCurrentParentItem = (defaultFriendlyURL?: string) => {
    const { path: pagePath } = usePageContext();
    const getFilteredItems = useFilteredItems();

    const friendlyURL = defaultFriendlyURL || getFriendlyUrl(pagePath);

    const find = (items: IMenuItemConfig[], path: string) => items.find((item) =>
        item.url === path
        || deepFind({ items: item.children, childrenKey: 'children', sourceKey: 'friendlyURL', source: path })
    );

    return getFilteredItems(find, friendlyURL);
};

export const useCurrentParentItems = (defaultFriendlyURL?: string) => {
    const menuItems = useAppSelector(prop('menuItems'));
    const { path: pagePath } = usePageContext();
    const getFilteredItems = useFilteredItems();
    const [dTagChildGamesCategories] = useDynamicTags([DYNAMIC_TAG_NAMES.childGamesCategories]);

    const friendlyURL = defaultFriendlyURL || getFriendlyUrl(pagePath);

    return useMemo(() => {
        const splitUrl = friendlyURL.split('/');

        if (splitUrl.length > 1 && dTagChildGamesCategories?.includes(splitUrl[0])) {
            const item = menuItems.find(menuItem => menuItem?.friendlyURL === `/${splitUrl[0]}`);

            return [item];
        }

        const find = (items: IMenuItemConfig[], source: string): [] | IMenuItemConfig[] | null => {
            let result;
            let mItem;

            for (let i = 0, len = items.length; i < len; i++) {
                mItem = items[i];

                if (mItem.friendlyURL === source) {
                    return [];
                }

                if (mItem.children) {
                    result = find(mItem.children, source);
                }

                if (result) {
                    result.unshift(mItem);
                    return result;
                }
            }

            return null;
        };

        return getFilteredItems(find, friendlyURL) || [];
    }, [dTagChildGamesCategories, friendlyURL, getFilteredItems, menuItems]);
};
